import React, { useState } from 'react';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getVoucherStockWarningApi, trimVendoVouchersApi, type VoucherStockVendo } from '@/api/vendo';

/**
 * The voucher stock banner on the owner's home page (Ray, 2026-10-20).
 *
 * One banner, plain English. It shows when a vendo holds more unsold counter
 * vouchers than its router keeps (the newest 500); older codes work only while
 * SmartFi is online. With several vendos a toggle lists them. "Your router is
 * getting slow" is added only when the router measured slow. "Delete old
 * vouchers" trims a vendo's oldest unsold codes after an in-banner confirm:
 * printed-but-unsold paper stops working, so the operator says yes first.
 */

const Box = styled.div`
   background: #fff7e6;
   border: 1px solid #f5c56b;
   border-radius: 12px;
   padding: 12px 14px;
   margin-bottom: 14px;
   @media (max-width: 520px) { padding: 8px 10px; border-radius: 10px; }
`;
const Row = styled.div`
   display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
   @media (max-width: 520px) { gap: 8px; flex-wrap: nowrap; }
`;
const Icon = styled.div`
   width: 34px; height: 34px; border-radius: 50%; background: #fde8b5; color: #8a5a00;
   display: flex; align-items: center; justify-content: center; font-weight: 700; flex: 0 0 auto;
   @media (max-width: 520px) { width: 22px; height: 22px; font-size: 12px; }
`;
const Text = styled.div` flex: 1 1 320px; min-width: 240px; @media (max-width: 520px) { min-width: 0; flex: 1 1 auto; } `;
const Title = styled.div` font-weight: 700; color: #8a5a00; @media (max-width: 520px) { font-size: 13px; line-height: 1.25; } `;
const Desc = styled.div`
   color: #5b4a1f; font-size: 14px; margin-top: 2px;
   @media (max-width: 520px) { font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;
const Btn = styled.button`
   background: #2563eb; color: #fff; border: 0; border-radius: 8px; padding: 8px 12px; font-weight: 600; font-size: 14px; cursor: pointer; flex: 0 0 auto;
   &:disabled { opacity: .6; cursor: default; }
   @media (max-width: 520px) { padding: 6px 9px; font-size: 12px; border-radius: 7px; }
`;
const Ghost = styled(Btn)` background: transparent; color: #8a5a00; border: 1px solid #f5c56b; `;
const Toggle = styled.button`
   background: none; border: 0; color: #8a5a00; font-weight: 600; font-size: 14px; cursor: pointer; padding: 6px 0 0;
   @media (max-width: 520px) { font-size: 12px; padding-top: 4px; }
`;
const List = styled.div` margin-top: 8px; border-top: 1px solid #f5c56b; `;
const Item = styled.div`
   display: grid; grid-template-columns: 1fr auto auto; gap: 10px; align-items: center; padding: 9px 0; border-bottom: 1px dashed #f1dca8; font-size: 14px;
   &:last-child { border-bottom: 0; }
   @media (max-width: 520px) { gap: 6px; padding: 5px 0; font-size: 12px; }
`;
const Name = styled.span` font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; span { color: #6b7280; font-weight: 400; } `;
const Num = styled.span` color: #5b4a1f; white-space: nowrap; `;
const Link = styled.button` background: none; border: 0; color: #2563eb; font-weight: 600; cursor: pointer; padding: 0; white-space: nowrap; font-size: inherit; `;
const Confirm = styled.div` margin-top: 8px; padding: 10px; border-radius: 8px; background: #fff; border: 1px solid #f5c56b; font-size: 14px; display: flex; gap: 8px; flex-wrap: wrap; align-items: center; `;

const fmt = (n: number) => n.toLocaleString('en-US');

export function bannerTitle(vendos: VoucherStockVendo[]): string {
   const slow = vendos.some((v) => v.slow);
   const head = vendos.length === 1
      ? `${vendos[0].name} has ${fmt(vendos[0].unused)} unused vouchers`
      : `Too many unused vouchers on ${vendos.length} vendos`;
   return slow ? `${head} — your router is getting slow` : head;
}

export function bannerLine(limit: number, vendos: VoucherStockVendo[]): string {
   const only = vendos.length === 1 ? `The other ${fmt(vendos[0].onlineOnly)} work only when SmartFi is online.` : 'Older vouchers work only when SmartFi is online.';
   return `The router keeps only the newest ${fmt(limit)}. ${only} Delete old vouchers you no longer sell.`;
}

export interface VoucherStockBannerProps {
   limit: number;
   vendos: VoucherStockVendo[];
   onTrim: (vendo: VoucherStockVendo) => Promise<void> | void;
   trimming?: string | null;
}

/** The banner itself (no fetching), so it can be rendered and tested with data. */
export function VoucherStockBanner({ limit, vendos, onTrim, trimming }: VoucherStockBannerProps) {
   const [open, setOpen] = useState(false);
   const [confirm, setConfirm] = useState<VoucherStockVendo | null>(null);
   if (!vendos.length) return null;
   const ask = (v: VoucherStockVendo) => setConfirm(v);
   return (
      <Box role="status" aria-live="polite" data-testid="voucher-stock-banner">
         <Row>
            <Icon aria-hidden>!</Icon>
            <Text>
               <Title>{bannerTitle(vendos)}</Title>
               <Desc>{bannerLine(limit, vendos)}</Desc>
            </Text>
            <Btn type="button" onClick={() => (vendos.length === 1 ? ask(vendos[0]) : setOpen(true))}>Delete old vouchers</Btn>
         </Row>
         {vendos.length > 1 && (
            <Toggle type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
               {open ? '▼' : '▶'} {open ? 'Hide' : 'See'} the {vendos.length} vendos
            </Toggle>
         )}
         {vendos.length > 1 && open && (
            <List>
               {vendos.map((v) => (
                  <Item key={v.id}>
                     <Name>{v.name}{v.parentName ? <span> · sub-vendo of {v.parentName}</span> : <span> · main</span>}</Name>
                     <Num>{fmt(v.unused)} unused</Num>
                     <Link type="button" onClick={() => ask(v)} disabled={trimming === v.id}>{trimming === v.id ? 'Deleting…' : 'Delete old'}</Link>
                  </Item>
               ))}
            </List>
         )}
         {confirm && (
            <Confirm role="alertdialog">
               <span>
                  Delete the {fmt(confirm.onlineOnly)} oldest unused vouchers of <strong>{confirm.name}</strong>? The newest {fmt(limit)} stay.
                  If you printed the old ones, they will stop working.
               </span>
               <Btn type="button" disabled={trimming === confirm.id} onClick={async () => { const v = confirm; setConfirm(null); await onTrim(v); }}>
                  Yes, delete {fmt(confirm.onlineOnly)}
               </Btn>
               <Ghost type="button" onClick={() => setConfirm(null)}>No</Ghost>
            </Confirm>
         )}
      </Box>
   );
}

/** The live banner on the home page: fetches the owner's vendos over the window and trims on confirm. */
export default function VoucherStockBannerLive() {
   const qc = useQueryClient();
   const [trimming, setTrimming] = useState<string | null>(null);
   const { data } = useQuery({ queryKey: ['voucher-stock-warning'], queryFn: getVoucherStockWarningApi, staleTime: 60_000, retry: false });
   if (!data || !data.vendos.length) return null;
   const onTrim = async (v: VoucherStockVendo) => {
      setTrimming(v.id);
      try {
         const r = await trimVendoVouchersApi(v.id);
         toast.success(`Deleted ${fmt(r.deleted)} old vouchers of ${v.name}.`);
         await qc.invalidateQueries({ queryKey: ['voucher-stock-warning'] });
      } catch (e: any) {
         toast.error(e?.response?.data?.error || 'Could not delete the old vouchers. Try again.');
      } finally {
         setTrimming(null);
      }
   };
   return <VoucherStockBanner limit={data.limit} vendos={data.vendos} onTrim={onTrim} trimming={trimming} />;
}
